import { Contract } from '@ekt-group/general-purpose-api-interfaces';
import { FileQuestion02 } from '@untitled-ui/icons-react';
import { Skeleton } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import CustomBadge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import { useGetContracts } from '@/features/contracts/api/use-get-contracts';
import { useGetContractsSaldo } from '@/features/invoices/api/use-get-saldo';
import ContractPaymentModal from '@/features/invoices/components/ContractPaymentModal';
import { formatAsCurrency } from '@/helpers/number-formatter';
import { useUserInfoStore } from '@/store';

const InvoicesActionCard = () => {
  const { t } = useTranslation('desktopPage');
  const { selectedAccount } = useUserInfoStore();
  const navigate = useNavigate();

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const contractsQuery = useGetContracts({
    params: {
      disablePagination: true,
      ownerCodes: [selectedAccount?.code],
    },
    queryConfig: {
      enabled: !!selectedAccount?.code,
    },
  });
  const contracts = contractsQuery.data as Contract[];

  const contractsSaldoQuery = useGetContractsSaldo({
    params: {
      contractNumbers: contracts?.map(({ contractNumber }) => contractNumber),
    },
    queryConfig: {
      enabled: !!contracts?.length,
    },
  });

  const total = contractsSaldoQuery.data?.data?.reduce(
    (acc, item) => {
      acc.amount += item.amount || 0;
      acc.amountOver += item.amountOver || 0;
      return acc;
    },
    { amount: 0, amountOver: 0 },
  );

  const isLoading = contractsQuery.isLoading || contractsSaldoQuery.isLoading;

  if (isLoading) {
    return (
      <div className="bg-white h-[218px] sm:h-auto sm:flex-1 flex flex-col justify-between rounded-[32px] p-5">
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  }

  return (
    <div className="bg-white h-[218px] sm:h-auto flex flex-col justify-between rounded-[32px] sm:flex-1 p-5">
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <span className="text-[20px] text-[#595959] font-bold">{t('invoices', { ns: 'header' })}</span>
          <FileQuestion02 className="w-6 h-6 text-[#9C8B7E]" />
        </div>
        <div className="flex flex-col items-start space-y-2 mb-4">
          {total?.amount < 0 && (
            <CustomBadge variant="success">
              {t('prePayment', { ns: 'invoicesPage' })}: {formatAsCurrency(total.amount * -1)}
            </CustomBadge>
          )}

          {total?.amount === 0 && <CustomBadge variant="success">{t('allPaid')}</CustomBadge>}

          {total?.amount > 0 && (
            <CustomBadge variant="warning">
              {t('upcoming')}: {formatAsCurrency(total.amount)}
            </CustomBadge>
          )}

          {Boolean(total?.amountOver) && (
            <CustomBadge variant="danger">
              {t('overdue')}: {formatAsCurrency(total.amountOver)}
            </CustomBadge>
          )}
        </div>
      </div>
      {total?.amountOver || total?.amount ? (
        <Button onClick={() => setIsPaymentModalOpen(true)} className="w-full">
          {t('payAll')}
        </Button>
      ) : (
        <Button variant="primaryLight" onClick={() => navigate('/invoices')}>
          {t('viewAll')}
        </Button>
      )}
      <ContractPaymentModal isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} />
    </div>
  );
};

export default InvoicesActionCard;
